/**
 * Auth helper — JWT obtain / refresh / logout against the Django backend.
 *
 * Endpoints come from `super_admin/api_urls.py` and are mounted at
 * `/api/super_admin/...` on BOTH the public host and every tenant subdomain
 * (see `taskydo/urls_public.py` + `taskydo/urls_tenant.py`).
 *
 *   POST /api/super_admin/token/          -> CustomTokenObtainPairView
 *       body: { email, password }         -> { access, refresh }
 *   POST /api/super_admin/token/refresh/  -> SimpleJWT TokenRefreshView
 *       body: { refresh }                 -> { access }
 *
 * NOTE — credential field name.
 * The Django User model declares `USERNAME_FIELD = 'email'`
 * (see `super_admin/models.py`).  SimpleJWT's TokenObtainPairSerializer
 * uses that field name, so the request body key is `email`.  The *value*
 * may be an email, a username, or a phone number — the backend resolves
 * all three.  We keep the JS parameter named `username` for generic auth
 * UX terminology, but we post it under the `email` key.
 *
 * Multi-tenant note: do NOT pass an organisation / tenant in the request.
 * The backend resolves the tenant from the request's `Host` header, which
 * the browser sets automatically based on the subdomain you're loaded from.
 */
import api, { TOKEN_ENDPOINTS } from './axios';
import { STORAGE_KEYS } from '../config';
import { canonicalizeLoginIdentifier } from '../lib/loginIdentifier';
import { markSignedIn, markSignedOut, writeTokens } from '../lib/authStorage';

/**
 * Obtain a fresh access+refresh pair for `(username, password)` and persist
 * them in localStorage.  Throws on bad credentials.
 *
 * `username` here is the user's email, username, or phone number.  It is
 * posted under the `email` key because USERNAME_FIELD is still `email`.
 *
 * Returns the raw response data so callers can chain a follow-up `/me/` if
 * they want the user record.
 */
export async function login(username, password, extras = {}) {
  // Body key is `email` because USERNAME_FIELD='email' on super_admin.User.
  // The value may be an email, a username, or a phone number.
  const { data } = await api.post(TOKEN_ENDPOINTS.OBTAIN, {
    email: canonicalizeLoginIdentifier(username),
    password,
    ...extras,
  });

  markSignedIn();
  writeTokens({
    access: data && data.access,
    refresh: data && data.refresh,
  });
  return data;
}


/**
 * Wipe local auth state.  Returns void.  We do NOT call a server-side
 * logout endpoint here because JWT logout is a no-op on the server (the
 * token simply expires).  If you also want to invalidate the Django
 * session cookie, call the existing `AuthAPI.logout()` from `lib/api.js`.
 */
export function logout() {
  markSignedOut();
}

/** Convenience: are we currently holding an access token? */
export function isAuthenticated() {
  return !!localStorage.getItem(STORAGE_KEYS.ACCESS);
}

export default { login, logout, isAuthenticated };
